import { useTranslation } from 'react-i18next'

import { InputSection, LabelSize } from 'aem-libs-react-components/src/components/calculators/components/input-section'
import { DateField } from 'aem-libs-react-components/src/components/forms/date-field'
import { PlusMinusNumberField } from 'aem-libs-react-components/src/components/forms/plus-minus-number-field'
import { TextField } from 'aem-libs-react-components/src/components/forms/text-field/TextField'
import { ToggleButtons } from 'aem-libs-react-components/src/components/forms/toggle-buttons'
import { useDynamicParams } from 'aem-libs-react-ts/src/hooks/useDynamicParams'

import { ACC_NUMERIC_PATTERN, MAX_ACC_CHARS, trackingKeys } from '../constants'
import { testIds } from '../e2e/testIds'
import { usePaymentDisabledDateCheck } from '../hooks/usePaymentDisabledDateCheck'
import { earlyRepaymentStore } from '../store'
import type { EarlyRepaymentParams } from '../types'

export const InputFields = () => {
  const { t } = useTranslation()
  const { repaymentInputConfig } = useDynamicParams<EarlyRepaymentParams>()
  const { isPaymentDisabledDate } = usePaymentDisabledDateCheck()

  const values = earlyRepaymentStore.useStoreData()
  const actions = earlyRepaymentStore.useStoreActions()

  return (
    <InputSection.Container>
      <InputSection.Row
        id={trackingKeys.extraordinaryInstallmentId}
        tip={{ text: t('ACCOUNT_NUMBER_TOOLTIP') }}
        labelSize={LabelSize.MEDIUM_LARGE}
        label={t('ACCOUNT_NUMBER_LABEL')}
      >
        <TextField
          {...repaymentInputConfig}
          value={values.accnr}
          onChangeComplete={actions.setAccnr}
          width={'extraLong'}
          type='tel'
          pattern={ACC_NUMERIC_PATTERN}
          maxLength={MAX_ACC_CHARS}
          testId={testIds.accountNumberInputBase}
        />
      </InputSection.Row>
      <InputSection.Row label={t('ENTIRE_LOAN_CHECKBOX_LABEL')} labelSize={LabelSize.MEDIUM_LARGE}>
        <ToggleButtons
          aria-label={t('ENTIRE_LOAN_CHECKBOX_LABEL')}
          activeValue={(values.isWillingToRepayWholeAmount ? 'YES' : 'NO') as string}
          testId={testIds.wholeAmountToggleBase}
          items={[
            {
              label: t('ENTIRE_LOAN.YES'),
              value: 'YES'
            },
            {
              label: t('ENTIRE_LOAN.NO'),
              value: 'NO'
            }
          ]}
          onChange={(value) => actions.setWillingToRepayWholeAmount(value === 'YES')}
          responsive
        />
      </InputSection.Row>
      <InputSection.Row
        id={trackingKeys.extraordinaryInstallmentId}
        labelSize={LabelSize.MEDIUM_LARGE}
        label={t('AMOUNT_LABEL')}
      >
        <PlusMinusNumberField
          {...repaymentInputConfig}
          unitType='czk'
          disabled={values.isWillingToRepayWholeAmount}
          value={values.amount || 0}
          onChangeComplete={actions.setAmount}
          width={'extraLong'}
          trackingDescription={trackingKeys.extraordinaryInstallment}
          id={trackingKeys.extraordinaryInstallmentId}
          testId={testIds.installmentAmountInputBase}
        />
      </InputSection.Row>
      <InputSection.Row
        id={trackingKeys.extraordinaryInstallmentId}
        labelSize={LabelSize.MEDIUM_LARGE}
        label={t('DATE_LABEL')}
      >
        <DateField
          id='paymentDateToId'
          width={'extraLong'}
          disableDates={isPaymentDisabledDate}
          value={values.date ? new Date(values.date) : new Date()}
          onChange={actions.setDate}
          testId={testIds.paymentDateInput}
          responsive
        />
      </InputSection.Row>
    </InputSection.Container>
  )
}
